import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useLanguageStore } from './languageStore';

export interface User {
  id: string;
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  phonenumber?: string;
  full_name: string;
  role: string;
  // Tenant the user belongs to. Returned by the backend `user.to_dict()`.
  // Null/undefined for legacy users created before multi-tenancy.
  tenant_id?: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  // Plan Information
  selected_plan: string;
  plan_adopted_date: string;
  plan_expiration_date?: string;
  plan_status: string;
  // Usage Information
  monthly_document_limit: number;
  monthly_documents_used: number;
  monthly_reset_date: string;
  // Feature Access
  has_advanced_extraction: boolean;
  has_document_tampering_detection: boolean;
  has_workflow_automation: boolean;
  has_custom_field_extraction: boolean;
  has_priority_support: boolean;
  has_unlimited_users: boolean;
  has_custom_api_development: boolean;
  has_tariff_feedback: boolean;
  data_retention_days: number;
  // User Preferences
  preferred_language: string;
  enable_compliance_checks: boolean;
  // Sidebar route the user has pinned as their post-login landing.
  pinned_nav_path?: string | null;
  // Payment Information
  payment_method?: string;
  billing_address?: string;
  payment_status: string;
  last_payment_date?: string;
  next_billing_date?: string;
  payment_reference?: string;
}

export interface Token {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface AuthState {
  user: User | null;
  token: Token | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setAuth: (user: User, token: Token) => void;
  clearAuth: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: User) => void;
  updateToken: (token: Token) => void;

  // Role selectors. Computed from the persisted user object so callers
  // (route guards, nav gating) share a single source of truth. Legacy global
  // admins (`is_admin`) are treated as super admins.
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      setAuth: (user: User, token: Token) => {
        // Sync user's preferred language with language store
        const { setLanguage } = useLanguageStore.getState();
        if (user.preferred_language) {
          setLanguage(user.preferred_language as 'en' | 'de');
        }

        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearAuth: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setLoading: (loading: boolean) =>
        set({ isLoading: loading }),

      updateUser: (user: User) => {
        // Sync updated user's preferred language with language store
        const { setLanguage } = useLanguageStore.getState();
        if (user.preferred_language) {
          setLanguage(user.preferred_language as 'en' | 'de');
        }
        set({ user });
      },

      updateToken: (token: Token) =>
        set({ token }),

      isSuperAdmin: () => {
        const { user } = get();
        return user?.role === 'super_admin' || user?.is_admin === true;
      },

      isTenantAdmin: () => {
        const { user } = get();
        return user?.role === 'tenant_admin';
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);